import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useActions } from '../../../hooks/useActions';
import { useNavigateSearch } from '../../../hooks/useNavigateSearch';

import { OrganizationType } from '../../../models/enums/OrganizationType';
import {
  ProductSubscriptionOverviewPageParams,
  RoutePath,
} from '../../../models/enums/RoutePath';
import { EditProductSubscriptionPageParamsType } from '../../../models/types/RoutePath';
import { clearOrganizationContracts } from '../../../store/redux-slices/contracts';
import {
  organizationNameSelector,
  organizationTypeSelector,
} from '../../../store/redux-slices/organizations/selectors';
import { clearSubscriptionHelperData } from '../../../store/redux-slices/subscriptions/common';
import {
  clearKECurrentSubscriptionEditing,
  clearKESubscriptionHelperData,
  clearKESubscriptionOverview,
} from '../../../store/redux-slices/subscriptions/kidnapAndExtortion';
import { keSubscriptionOverviewSelector } from '../../../store/redux-slices/subscriptions/kidnapAndExtortion/selectors';
import { loadOrganizationContracts } from '../../../store/saga-slices/contracts';
import { ILoadOrganizationContractsPayload } from '../../../store/saga-slices/contracts/models';
import { loadOrganization } from '../../../store/saga-slices/organizations';
import {
  ILoadEditingAASubscriptionPayload,
  ILoadSubscriptionOverviewPayload,
} from '../../../store/saga-slices/subscriptions/airAmbulance/models';
import { ILoadDatesForNewSubscriptionPayload } from '../../../store/saga-slices/subscriptions/common/models';
import {
  loadDatesForNewKESubscription,
  loadEditingKESubscription,
  loadKESubscriptionOverview,
} from '../../../store/saga-slices/subscriptions/kidnapAndExtortion';

export const useEditKESubscriptionPage = () => {
  const navigate = useNavigateSearch();

  const LoadOrganizationContracts = useActions(loadOrganizationContracts);
  const LoadDatesForNewSubscription = useActions(loadDatesForNewKESubscription);
  const LoadSubscriptionOverview = useActions(loadKESubscriptionOverview);
  const ClearOrganizationContracts = useActions(clearOrganizationContracts);
  const LoadOrganization = useActions(loadOrganization);
  const LoadEditingSubscription = useActions(loadEditingKESubscription);
  const ClearKESubscriptionHelperData = useActions(
    clearKESubscriptionHelperData,
  );
  const ClearSubscriptionHelperData = useActions(clearSubscriptionHelperData);
  const ClearSubscriptionOverview = useActions(clearKESubscriptionOverview);
  const ClearCurrentSubscriptionEditing = useActions(
    clearKECurrentSubscriptionEditing,
  );

  const organizationName = useSelector(organizationNameSelector);
  const organizationType = useSelector(organizationTypeSelector);
  const subscriptionOverview = useSelector(keSubscriptionOverviewSelector);

  const { organizationId, subscriptionId, subscriptionType } =
    useParams<EditProductSubscriptionPageParamsType>();

  useEffect(() => {
    const payload: ILoadSubscriptionOverviewPayload = {
      organizationId,
      subscriptionId,
    };
    LoadSubscriptionOverview(payload);
  }, [LoadSubscriptionOverview, organizationId, subscriptionId]);

  useEffect(() => {
    if (subscriptionOverview && !subscriptionOverview.isDraft) {
      navigate(
        RoutePath.ClientOverviewProductSubscriptionOverviewPageFullPath,
        {
          params: {
            [ProductSubscriptionOverviewPageParams.OrganizationId]:
              organizationId,
            [ProductSubscriptionOverviewPageParams.SubscriptionId]:
              subscriptionId,
            [ProductSubscriptionOverviewPageParams.SubscriptionType]:
              subscriptionType,
          },
        },
      );
    }
  }, [
    navigate,
    organizationId,
    subscriptionId,
    subscriptionOverview,
    subscriptionType,
  ]);

  useEffect(() => {
    const payload: ILoadEditingAASubscriptionPayload = {
      organizationId,
      subscriptionId,
      subscriptionType,
    };

    LoadEditingSubscription(payload);
  }, [
    LoadEditingSubscription,
    organizationId,
    subscriptionId,
    subscriptionType,
  ]);

  const loadDatesForNewSubscriptionHandler = useCallback(
    (value: string, forceLoad = false) => {
      if (!value && !forceLoad) {
        return;
      }

      const loadDatesForNewSubscriptionPayload: ILoadDatesForNewSubscriptionPayload =
        {
          organizationId,
          contractId: value,
        };
      LoadDatesForNewSubscription(loadDatesForNewSubscriptionPayload);
    },
    [LoadDatesForNewSubscription, organizationId],
  );

  useEffect(() => {
    if (
      organizationType === OrganizationType.Agent ||
      organizationType === OrganizationType.Group
    ) {
      navigate(RoutePath.ClientOverviewGeneralPageFullPath, {
        params: { id: organizationId },
      });
    }
  }, [navigate, organizationId, organizationType]);

  useEffect(() => {
    const loadOrganizationContractsPayload: ILoadOrganizationContractsPayload =
      {
        organizationId,
      };

    LoadOrganizationContracts(loadOrganizationContractsPayload);
    LoadOrganization(organizationId);
  }, [
    LoadDatesForNewSubscription,
    LoadOrganization,
    LoadOrganizationContracts,
    organizationId,
  ]);

  useEffect(
    () => () => {
      ClearOrganizationContracts();
      ClearKESubscriptionHelperData();
      ClearSubscriptionOverview();
      ClearCurrentSubscriptionEditing();
      ClearSubscriptionHelperData();
    },
    [
      ClearKESubscriptionHelperData,
      ClearOrganizationContracts,
      ClearSubscriptionOverview,
      ClearCurrentSubscriptionEditing,
      ClearSubscriptionHelperData,
    ],
  );

  return {
    organizationName,
    organizationId,
    loadDatesForNewSubscriptionHandler,
    subscriptionType,
  };
};
